import { useQuery } from "@tanstack/react-query"
import {
  isFormModuleNameAvailable,
  isSessionTemplateNameAvailable,
} from "../api"
import { useDebounce } from "../../../hooks/useDebounce"

// Long enough that a name is checked once typing pauses rather than per
// keystroke, short enough that the warning lands before the mouse reaches Save.
const NAME_CHECK_DEBOUNCE_MS = 400

/** The names a check is made against: one record type's, by its endpoint. */
export interface NameCheck {
  key: string
  isAvailable: (
    name: string,
    signal: AbortSignal,
    ownId?: string,
  ) => Promise<boolean>
  source: string
}

export const MODULE_NAMES: NameCheck = {
  key: "form-module-name-available",
  isAvailable: isFormModuleNameAvailable,
  source: "isFormModuleNameAvailable",
}

export const TEMPLATE_NAMES: NameCheck = {
  key: "session-template-name-available",
  isAvailable: isSessionTemplateNameAvailable,
  source: "isSessionTemplateNameAvailable",
}

/**
 * Whether the name being typed is held by another record of its kind (#414
 * AC 1.1). Advisory, so the administrator is warned while typing rather than
 * on save: the save carries the real guard, since two administrators can both
 * be told a name is free before either saves. A check that fails is left to
 * the cache to record; the absence of a verdict reads as "not taken".
 *
 * `ownName` is the name the record being edited already holds. Keeping it,
 * whatever the case, is not a clash, so it is neither asked about nor answered
 * from an earlier verdict the cache still holds for it. `ownId` is the record
 * itself, sent so the server counts no name it holds elsewhere — a module's
 * published name, while its working copy is renamed — against it.
 */
export function useNameTaken(
  check: NameCheck,
  name: string,
  ownName?: string,
  ownId?: string,
): boolean {
  const trimmed = name.trim()
  const settled = useDebounce(trimmed, NAME_CHECK_DEBOUNCE_MS)
  const own =
    ownName !== undefined && settled.toLowerCase() === ownName.toLowerCase()

  const availability = useQuery({
    queryKey: [check.key, settled, ownId ?? null],
    queryFn: ({ signal }) => check.isAvailable(settled, signal, ownId),
    enabled: settled !== "" && !own,
    retry: false,
    meta: { source: check.source },
  })

  // A verdict is only about the name it was asked for: while the typed name
  // has moved on from the checked one there is none, and the warning clears
  // on the keystroke rather than when the next check lands.
  return trimmed === settled && !own && availability.data === false
}
